/**
 * Profit per truck.
 *
 * The reason the chart has a cost group for every truck. Revenue is the
 * trips the truck ran; costs are everything posted to its group, plus
 * hire charges for a hired truck. Best first, so the question "which
 * truck earns, which costs" is answered by the first and last rows.
 *
 * Costs on no truck (site car, office, rent) are one figure underneath,
 * never spread over the trucks: any split would be a guess, and a guess
 * that moves a truck from profit to loss is worse than no split.
 */
import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Alert, Box, Button, Chip, Collapse, Skeleton, Stack, Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'

import { fetchProfitability } from '../api/endpoints'
import { P, type VehicleProfit as Row } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { c, fmtMoney } from '../theme'
import ReportShell, { rangeLabel } from '../reports/ReportShell'
import ReportFooter from '../reports/ReportFooter'
import ReportToolbar from '../reports/ReportToolbar'
import { useDateRange } from '../reports/useDateRange'
import { downloadCsv } from '../reports/csv'

const tone = (v: string | null) =>
  v !== null && Number(v) < 0 ? c.dangerInk : undefined

export default function VehicleProfit() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const nav = useNavigate()
  const { range, setRange, presets } = useDateRange()
  const [open, setOpen] = useState<number | null>(null)

  const q = useQuery({
    queryKey: ['profitability', range.from, range.to],
    queryFn: () => fetchProfitability({ from_date: range.from, to_date: range.to }),
  })
  const p = q.data
  const rows = p?.vehicles ?? []

  return (
    <ReportShell titleKey="nav.vehicleProfit"
      period={rangeLabel(range.from, range.to, i18n.language, t)}
      toolbar={
        <ReportToolbar range={range} setRange={setRange} presets={presets}
          pdf={{ path: 'vehicle-profitability.pdf',
                 params: { from_date: range.from, to_date: range.to },
                 filename: `vehicle-profitability-${range.from}-${range.to}.pdf` }}
          onExport={() => downloadCsv(
            `vehicle-profitability-${range.from}-${range.to}`, [
              ['Vehicle', 'Trips', 'Revenue', 'Costs', 'Profit', 'Margin %',
               'Cost per trip', 'Fuel', 'Fuel per trip'],
              ...rows.map((v) => [v.plate_no, v.trips, v.revenue, v.cost,
                                  v.profit, v.margin ?? '', v.cost_per_trip ?? '',
                                  v.fuel, v.fuel_per_trip ?? '']),
            ])} />
      }>

      {p && p.unlinked_vehicles.length > 0 && (
        <Alert severity="warning" sx={{ mx: 2, mt: 1.5 }}
          action={<Button size="small" onClick={() => nav('/transport/fleet')}>
            {t('nav.fleet')}</Button>}>
          {t('profit.unlinked', { plates: p.unlinked_vehicles.join(', ') })}
        </Alert>
      )}

      {p && (
        <Box sx={{ pt: 1.5 }}>
          <FigureGrid>
            <Figure label={bi('profit.revenue')} value={fmtMoney(p.totals.revenue)} />
            <Figure label={bi('profit.costs')} value={fmtMoney(p.totals.cost)} />
            <Figure label={bi('profit.otherCosts')} value={fmtMoney(p.other_costs)}
              tone={Number(p.other_costs) < 0 ? 'danger' : undefined} />
            <Figure label={bi('profit.trucksProfit')} value={fmtMoney(p.totals.profit)} strong />
          </FigureGrid>
        </Box>
      )}

      {q.isLoading ? (
        <Box sx={{ p: 2 }}><Skeleton /><Skeleton /><Skeleton /></Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 3 }}>
          {t('report.nothingInRange')}
        </Typography>
      ) : (
        <Box sx={{ px: 2, pb: 1, overflowX: 'auto' }}>
          <table className="rpt" style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th>{t('trip.vehicle')}</th>
                <th className="num">{t('profit.trips')}</th>
                <th className="num">{t('profit.revenue')}</th>
                <th className="num">{t('profit.costs')}</th>
                <th className="num">{t('profit.profit')}</th>
                <th className="num">{t('profit.margin')}</th>
                <th className="num">{t('profit.costPerTrip')}</th>
                <th className="num">{t('profit.fuelPerTrip')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((v, i) => (
                <Fragment key={v.id}>
                  <tr className="clickable"
                    onClick={() => setOpen(open === v.id ? null : v.id)}>
                    <td>
                      <span className="num">{v.plate_no}</span>
                      {i === 0 && rows.length > 1 && Number(v.profit) > 0 && (
                        <Chip size="small" label={t('profit.best')}
                          sx={{ ml: 1, height: 16, fontSize: 9,
                                bgcolor: c.primarySoft, color: c.primaryInk }} />
                      )}
                      {i === rows.length - 1 && rows.length > 1 && (
                        <Chip size="small" label={t('profit.lowest')}
                          sx={{ ml: 1, height: 16, fontSize: 9,
                                bgcolor: c.dangerSoft, color: c.dangerInk }} />
                      )}
                    </td>
                    <td className="num">{v.trips}</td>
                    <td className="num">{fmtMoney(v.revenue)}</td>
                    <td className="num">{fmtMoney(v.cost)}</td>
                    <td className="num" style={{ fontWeight: 600, color: tone(v.profit) }}>
                      {fmtMoney(v.profit)}</td>
                    <td className="num" style={{ color: tone(v.margin) }}>
                      {v.margin === null ? '—' : `${v.margin}%`}</td>
                    <td className="num">{v.cost_per_trip ? fmtMoney(v.cost_per_trip) : '—'}</td>
                    <td className="num">{v.fuel_per_trip ? fmtMoney(v.fuel_per_trip) : '—'}</td>
                  </tr>
                  <tr>
                    <td colSpan={8} style={{ padding: 0, border: 0 }}>
                      <Collapse in={open === v.id} unmountOnExit>
                        <Detail row={v} />
                      </Collapse>
                    </td>
                  </tr>
                </Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td>{t('report.total')}</td>
                <td className="num">{p!.totals.trips}</td>
                <td className="num">{fmtMoney(p!.totals.revenue)}</td>
                <td className="num">{fmtMoney(p!.totals.cost)}</td>
                <td className="num">{fmtMoney(p!.totals.profit)}</td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          </table>
        </Box>
      )}
      <Typography variant="caption"
        sx={{ display: 'block', px: 2, pb: 2, color: c.inkMuted }}>
        {t('profit.note')}
      </Typography>
      <ReportFooter />
    </ReportShell>
  )
}

function Detail({ row }: { row: Row }) {
  const { t } = useTranslation()
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}
      sx={{ px: 2, py: 1.5, bgcolor: c.surfacePage }}>
      <Box sx={{ flex: 1 }}>
        <Typography className="section-label" sx={{ mb: 0.5 }}>
          {t('profit.byAccount')}
          {row.cost_group_name ? ` — ${row.cost_group_name}` : ''}
        </Typography>
        <table className="rpt">
          <tbody>
            {row.ledgers.map((l) => (
              <tr key={l.code}>
                <td className="num" style={{ textAlign: 'start', width: 70 }}>{l.code}</td>
                <td>{l.name}</td>
                <td className="num">{fmtMoney(l.amount)}</td>
              </tr>
            ))}
            {Number(row.hire_cost) > 0 && (
              <tr><td /><td>{t('profit.hire')}</td>
                <td className="num">{fmtMoney(row.hire_cost)}</td></tr>
            )}
          </tbody>
        </table>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography className="section-label" sx={{ mb: 0.5 }}>
          {t('profit.byMonth')}
        </Typography>
        <table className="rpt">
          <thead>
            <tr>
              <th>{t('profit.month')}</th>
              <th className="num">{t('profit.trips')}</th>
              <th className="num">{t('profit.revenue')}</th>
              <th className="num">{t('profit.costs')}</th>
              <th className="num">{t('profit.profit')}</th>
            </tr>
          </thead>
          <tbody>
            {row.monthly.filter((m) => m.trips || Number(m.cost)).map((m) => (
              <tr key={m.month}>
                <td className="num" style={{ textAlign: 'start' }}>{m.month}</td>
                <td className="num">{m.trips}</td>
                <td className="num">{fmtMoney(m.revenue)}</td>
                <td className="num">{fmtMoney(m.cost)}</td>
                <td className="num" style={{ color: tone(m.profit) }}>
                  {fmtMoney(m.profit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Stack>
  )
}

/** Margins are financial information: the trip clerk does not see them. */
export function VehicleProfitPage() {
  const { t } = useTranslation()
  const { can } = useAuth()
  if (!can(P.reportFinancial) || !can(P.transportView)) {
    return <Alert severity="warning">{t('report.noPermission')}</Alert>
  }
  return <VehicleProfit />
}
